import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import type { ParameterGroupParameter } from 'Services/ProductManagement/Features/Parameter/types'
import { ProductPricingGrid } from 'Services/ProductManagement/Features/Product/Components/ProductPricingGrid'
import type { RowType } from 'Services/ProductManagement/Features/Product/Components/ProductPricingGrid/ProductPricingGrid'
import {
  type PricingPlan,
  PricingPlanTabType,
  type Product,
  type Validity
} from 'Services/ProductManagement/Features/Product/productSlice'
import type { ZoneDefinition } from 'Services/ProductManagement/Features/ZoneDefinition/types'
import { WALTTI_ENABLED_FEATURES } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Divider, Tab, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useState } from 'react'
import { t } from 'ttag'

interface ProductPricingTabsProps {
  pricingPlans: PricingPlan[]
  zoneDefinition: ZoneDefinition
  product: Product
  domicileClass: ParameterGroupParameter
  isEdited: boolean
  setIsEdited: (isEdited: boolean) => void
  handleOpenActivePeriod: () => void
  setProductPricingPlans: React.Dispatch<React.SetStateAction<PricingPlan[]>>
  handleSelectActivePeriod: (activePeriod: Validity) => void
  pricingPlanActivePeriod: Validity | null
  handleDeleteActivePeriod: (period: Validity | null) => void
  editedPricingPlanCells: RowType[]
  setEditedPricingPlanCells: React.Dispatch<React.SetStateAction<RowType[]>>
  updatedPricingPlanCells: RowType[]
  setUpdatedPricingPlanCells: React.Dispatch<React.SetStateAction<RowType[]>>
}

export default function ProductPricingTabs({
  pricingPlans,
  zoneDefinition,
  product,
  domicileClass,
  isEdited,
  setIsEdited,
  handleOpenActivePeriod,
  setProductPricingPlans,
  handleSelectActivePeriod,
  pricingPlanActivePeriod,
  handleDeleteActivePeriod,
  editedPricingPlanCells,
  setEditedPricingPlanCells,
  updatedPricingPlanCells,
  setUpdatedPricingPlanCells
}: Readonly<ProductPricingTabsProps>) {
  const { confirm } = useConfirm()
  const [drawerTab, setDrawerTab] = useState<PricingPlanTabType>(PricingPlanTabType.PRICINGS)

  const changeTab = (newValue: PricingPlanTabType) => {
    setIsEdited(false)
    setDrawerTab(newValue)
    setProductPricingPlans(product.pricing_plans)
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: PricingPlanTabType) => {
    if (isEdited) {
      confirm({
        title: t`Are you sure?`,
        message: t`Are you sure you want to continue? Unsaved changes will be lost.`,
        handleConfirm: () => {
          changeTab(newValue)
          setUpdatedPricingPlanCells([])
          setEditedPricingPlanCells([])
        }
      })
    } else {
      changeTab(newValue)
      setUpdatedPricingPlanCells([])
      setEditedPricingPlanCells([])
    }
  }

  return (
    <Box>
      {pricingPlanActivePeriod && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 2,
            pb: 1
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {pricingPlanActivePeriod.title}
          </Typography>
          <Box>
            <DeleteButton
              sx={{ mr: 1 }}
              size="small"
              onClick={() =>
                confirm({
                  title: t`Are you sure?`,
                  message: t`Are you sure you want to delete the pricing plan?`,
                  handleConfirm: () => {
                    handleDeleteActivePeriod(pricingPlanActivePeriod)
                  }
                })
              }
            >{t`Delete`}</DeleteButton>
            <CustomButton
              onClick={() => handleSelectActivePeriod(pricingPlanActivePeriod)}
              size="small"
              startIcon={<EditIcon />}
              variant="text"
            >{t`Edit`}</CustomButton>
          </Box>
        </Box>
      )}
      <TabContext value={drawerTab}>
        <TabList aria-label={t`Product tab list`} onChange={handleTabChange}>
          <Tab label={t`Pricings`} value={PricingPlanTabType.PRICINGS} />
          {WALTTI_ENABLED_FEATURES && (
            <Tab label={t`Exchange period`} value={PricingPlanTabType.EXCHANGE_PERIOD} />
          )}
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={PricingPlanTabType.PRICINGS}>
          <ProductPricingGrid
            key={`zoneDefinition-${pricingPlanActivePeriod?.title ?? 'default'}`}
            pricingPlans={pricingPlans}
            zoneDefinition={zoneDefinition}
            domicileClass={domicileClass.parameter_id}
            product={product}
            setIsEdited={setIsEdited}
            handleOpenActivePeriod={handleOpenActivePeriod}
            pricingPlanActivePeriod={pricingPlanActivePeriod}
            setProductPricingPlans={setProductPricingPlans}
            pricingGridType={PricingPlanTabType.PRICINGS}
            editedPricingPlanCells={editedPricingPlanCells}
            setEditedPricingPlanCells={setEditedPricingPlanCells}
            updatedPricingPlanCells={updatedPricingPlanCells}
          />
        </TabPanel>
        {WALTTI_ENABLED_FEATURES && (
          <TabPanel sx={{ p: 0 }} value={PricingPlanTabType.EXCHANGE_PERIOD}>
            <ProductPricingGrid
              key={`zoneDefinition-${pricingPlanActivePeriod?.title ?? 'default'}`}
              pricingPlans={pricingPlans}
              zoneDefinition={zoneDefinition}
              domicileClass={domicileClass.parameter_id}
              product={product}
              setIsEdited={setIsEdited}
              handleOpenActivePeriod={handleOpenActivePeriod}
              pricingPlanActivePeriod={pricingPlanActivePeriod}
              setProductPricingPlans={setProductPricingPlans}
              pricingGridType={PricingPlanTabType.EXCHANGE_PERIOD}
              editedPricingPlanCells={editedPricingPlanCells}
              setEditedPricingPlanCells={setEditedPricingPlanCells}
              updatedPricingPlanCells={updatedPricingPlanCells}
            />
          </TabPanel>
        )}
      </TabContext>
    </Box>
  )
}
